import React, { useEffect, useState } from 'react';
import { AlertCircle, Clock, Hand, Waves, Flame, Calendar, Beaker } from 'lucide-react';
import ShapingIcon from '../layout/ShapingIcon.tsx';
import { BakingSession, FERMENTATION_PROCESSES } from '../../types/baking';

interface StarterScheduleSectionProps {
  sessionData: Partial<BakingSession>;
  setSessionData: (data: Partial<BakingSession>) => void;
  existingSessions: BakingSession[];
}

export function StarterScheduleSection({ 
  sessionData, 
  setSessionData,
  existingSessions 
}: StarterScheduleSectionProps) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionData.targetBakeTime || !sessionData.fermentationType || !sessionData.starterId) {
      setSessionData(prev => ({
        ...prev,
        starterSchedule: {
          feeds: [],
          customized: false
        }
      }));
      return;
    }

    const process = FERMENTATION_PROCESSES.find(p => p.id === sessionData.fermentationType);
    if (!process) return;

    const newBakeTime = new Date(sessionData.targetBakeTime);
    const timeDiff = newBakeTime.getTime() - new Date().getTime();
    const hoursUntilBake = timeDiff / (1000 * 60 * 60);

    // If less than 9 hours until bake time, no feeds needed
    if (hoursUntilBake < 9) {
      setSessionData(prev => ({
        ...prev,
        starterSchedule: {
          feeds: [{
            id: 'no-feed',
            scheduledTime: new Date(),
            completed: false,
            noFeedsNeeded: true
          }],
          customized: false
        }
      }));
      return;
    }

    const kneadingTime = new Date(newBakeTime);
    kneadingTime.setHours(kneadingTime.getHours() - process.totalTime);

    // Find any existing sessions for this starter within 24 hours
    const recentSession = existingSessions
      .filter(session => 
        session.starterId === sessionData.starterId && 
        session.status !== 'cancelled'
      )
      .find(session => {
        const existingBakeTime = new Date(session.targetBakeTime);
        const timeDiff = Math.abs(newBakeTime.getTime() - existingBakeTime.getTime());
        return timeDiff <= 24 * 60 * 60 * 1000;
      });

    // Calculate feed times
    const feed3Time = new Date(kneadingTime);
    feed3Time.setHours(feed3Time.getHours() - 4);

    // If there's a recent session or time until bake is between 12-24h, only schedule feed #3
    if (recentSession || hoursUntilBake < 24) {
      setSessionData(prev => ({
        ...prev,
        starterSchedule: {
          feeds: [{ id: '3', scheduledTime: feed3Time, completed: false }],
          customized: false
        }
      }));
    } else {
      // Schedule all three feeds
      const feed2Time = new Date(feed3Time);
      feed2Time.setHours(feed2Time.getHours() - 8);

      const feed1Time = new Date(feed2Time);
      feed1Time.setHours(feed1Time.getHours() - 8);

      setSessionData(prev => ({
        ...prev,
        starterSchedule: {
          feeds: [
            { id: '1', scheduledTime: feed1Time, completed: false },
            { id: '2', scheduledTime: feed2Time, completed: false },
            { id: '3', scheduledTime: feed3Time, completed: false }
          ],
          customized: false
        }
      }));
    }
  }, [sessionData.targetBakeTime, sessionData.fermentationType, sessionData.starterId]);

  const handleFeedTimeChange = (feedId: string, newTime: string) => {
    const newDate = new Date(newTime);
    const feeds = [...(sessionData.starterSchedule?.feeds || [])];
    const feedIndex = feeds.findIndex(f => f.id === feedId);
    
    if (feedIndex === -1) return;

    // For Feed #3, ensure it's at least 3 hours before kneading time
    if (feedId === '3' && sessionData.targetBakeTime && sessionData.fermentationType) {
      const process = FERMENTATION_PROCESSES.find(p => p.id === sessionData.fermentationType);
      if (process) {
        const bakeTime = new Date(sessionData.targetBakeTime);
        const kneadingTime = new Date(bakeTime);
        kneadingTime.setHours(kneadingTime.getHours() - process.totalTime);

        if ((kneadingTime.getTime() - newDate.getTime()) < 3 * 60 * 60 * 1000) {
          setError('Feed #3 must be at least 3 hours before kneading time');
          return;
        }
      }
    }

    setError(null);
    feeds[feedIndex] = { ...feeds[feedIndex], scheduledTime: newDate };
    
    setSessionData(prev => ({
      ...prev,
      starterSchedule: {
        feeds,
        customized: true
      }
    }));
  };

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getKneadingTime = () => {
    if (!sessionData.targetBakeTime || !sessionData.fermentationType) return null;
    const process = FERMENTATION_PROCESSES.find(p => p.id === sessionData.fermentationType);
    if (!process) return null;

    const bakeTime = new Date(sessionData.targetBakeTime);
    const kneadingTime = new Date(bakeTime);
    
    // Use custom times if available, otherwise use process defaults
    let totalTime = process.totalTime;
    if (sessionData.fermentationType === 'cold-bulk' && sessionData.customBulkTime) {
      totalTime = sessionData.customBulkTime + process.proofTime;
    } else if (sessionData.fermentationType === 'pre-proofed' && sessionData.customProofTime) {
      totalTime = process.bulkTime + sessionData.customProofTime;
    }
    
    kneadingTime.setHours(kneadingTime.getHours() - totalTime);
    return kneadingTime;
  };

  if (!sessionData.starterSchedule?.feeds.length) return null;

  const kneadingTime = getKneadingTime();
  const hasRecentSession = sessionData.starterSchedule.feeds.length === 1;
  const noFeedsNeeded = sessionData.starterSchedule.feeds[0]?.noFeedsNeeded;
  const process = FERMENTATION_PROCESSES.find(p => p.id === sessionData.fermentationType);

  // Calculate all milestone times
  const bakeTime = sessionData.targetBakeTime ? new Date(sessionData.targetBakeTime) : null;
  const shapingTime = bakeTime && process ? new Date(bakeTime.getTime() - (
    sessionData.fermentationType === 'pre-proofed' && sessionData.customProofTime
      ? sessionData.customProofTime
      : process.proofTime
  ) * 60 * 60 * 1000) : null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-medium flex items-center gap-2 text-white mb-4">
          <Beaker className="w-5 h-5 text-amber-400" />
          Starter Feeding Schedule</h3>
        
        {noFeedsNeeded && (
          <div className="mb-6 p-4 bg-blue-500/10 backdrop-blur-sm rounded-lg border border-blue-500/20">
            <p className="text-sm text-blue-200">
              No feeding schedule needed for short fermentation
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 backdrop-blur-sm rounded-lg border border-red-500/20 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-300" />
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        {!noFeedsNeeded && <div className="space-y-4">
          {hasRecentSession ? (
            <>
              <div className="p-4 bg-white/5 rounded-xl border border-white/10 opacity-50">
                <h4 className="font-medium text-white mb-2">Feed #1</h4>
                <p className="text-sm text-white/70">
                  Not required
                </p>
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/10 opacity-50">
                <h4 className="font-medium text-white mb-2">Feed #2</h4>
                <p className="text-sm text-white/70">
                  Not required
                </p>
              </div>
              <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <h4 className="font-medium text-white mb-2">Feed #3</h4>
                <input
                  type="datetime-local"
                  value={new Date(new Date(sessionData.starterSchedule.feeds[0].scheduledTime).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                  onChange={(e) => {
                    handleFeedTimeChange('3', e.target.value);
                  }}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                />
                <p className="text-sm text-white/70 mt-2">
                  Scheduled for {formatDateTime(sessionData.starterSchedule.feeds[0].scheduledTime)}
                </p>
              </div>
            </>
          ) : (
            sessionData.starterSchedule.feeds.map((feed, index) => (
              <div key={feed.id} className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <h4 className="font-medium text-white mb-2">Feed #{index + 1}</h4>
                <input
                  type="datetime-local"
                  value={new Date(new Date(feed.scheduledTime).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                  onChange={(e) => {
                    handleFeedTimeChange(feed.id, e.target.value);
                  }}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                />
                <p className="text-sm text-white/70 mt-2">
                  Scheduled for {formatDateTime(feed.scheduledTime)}
                </p>
              </div>
            ))
          )}
        </div>}
      </div>

      {!noFeedsNeeded && <div className="p-4 bg-blue-500/10 backdrop-blur-sm rounded-lg border border-blue-500/20">
        <p className="text-sm text-blue-200">
          <strong>Note:</strong> Feed #3 must be completed at least 3 hours before kneading time.
        </p>
      </div>}

      {/* Session Timeline */}
      <div className="space-y-4">
        <h3 className="font-medium flex items-center gap-2 text-white mb-4">
          <Calendar className="w-5 h-5 text-amber-400" />
          Session Timeline</h3>
        
        <div className="relative space-y-4 p-4 bg-white/10 rounded-xl backdrop-blur-sm">
          {kneadingTime && (
            <div className="relative flex items-center gap-4 p-4 bg-blue-500/10 border border-white/20 hover:border hover:border-blue-500 hover:shadow-xl hover:shadow-white/10 shadow-xl rounded-xl z-10">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-500/50 rounded-full flex items-center justify-center">
                <Hand className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-medium text-white">Kneading</h4>
                <p className="text-sm text-white/70">
                  {formatDateTime(kneadingTime)}
                </p>
              </div>
            </div>
          )}

          {kneadingTime && (
              <p className="text-sm text-white mt-1 ml-20">
                <Clock className="w-6 h-6 inline mr-2" />
                   +{Math.round((shapingTime.getTime() - kneadingTime.getTime()) / (1000 * 60 * 60))}h (Bulk fermentation)
              </p>
          )} 
          
          {shapingTime && (
            <div className="relative flex items-center gap-4 p-4 bg-green-500/10 border border-white/20 hover:border hover:border-green-500 rounded-xl hover:shadow-xl hover:shadow-white/10 shadow-xl z-10">
              {/* Line connecting to previous step */}
              <div className="absolute left-10 -top-14 w-1 h-14 bg-white/60 z-0"></div>
              <div className="flex-shrink-0 w-12 h-12 bg-green-500/50 rounded-full flex items-center justify-center">
                <ShapingIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-medium text-white">Shaping</h4>
                <p className="text-sm text-white/70">
                  {formatDateTime(shapingTime)}
                </p>
              </div>
            </div>
          )}

          {shapingTime && (
              <p className="text-sm text-white mt-1 ml-20">
                <Clock className="w-6 h-6 inline mr-2" />
                    +{Math.round((bakeTime.getTime() - shapingTime.getTime()) / (1000 * 60 * 60))}h (Proofing)
              </p>
          )}
          
          {bakeTime && (
            <div className="relative flex items-center gap-4 p-4 bg-orange-500/10 border border-white/20 hover:border hover:border-orange-500 rounded-xl hover:shadow-xl hover:shadow-white/10 shadow-xl z-10">
              {/* Line connecting to previous step */}
              <div className="absolute left-10 -top-14 w-1 h-14 bg-white/60 z-0"></div>
              <div className="flex-shrink-0 w-12 h-12 bg-orange-500/50 rounded-full flex items-center justify-center">
                <Flame className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-medium text-white">Baking</h4>
                <p className="text-sm text-white/70">
                  {formatDateTime(bakeTime)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}